import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import { REQUEST_SANDBOX_TOOLTIP, SETUP_GUIDE_TOOLTIP } from 'helper/constant';
import { isRouteRestricted } from 'helper/utility';
import useRequestSandbox from 'hooks/useRequestSandbox';

import { selectLoggedInUser, selectIsSandboxAdminExist } from 'redux/selectors/auth';
import { selectSetupGuideStatus, selectUserRole } from 'redux/selectors/general';

import styles from '../../homeSetup.module.css';

import SetupAccordion from '../../components/SetupAccordion';
import { PROD_SETUP_STEPS } from '../../helper/constant';

const { GET_STARTED, TRY_OUT_PRODUCTS, GO_LIVE_READINESS } = PROD_SETUP_STEPS;

function ProdSetup() {
    const customer = useSelector(selectLoggedInUser);
    const guideStatus = useSelector(selectSetupGuideStatus);
    const userRole = useSelector(selectUserRole);
    const isSandboxAdminExist = useSelector(selectIsSandboxAdminExist);

    const requestSandbox = useRequestSandbox();

    // Send the sandbox request. The refreshed status (sentSandboxRequest: true) disables the
    // button via the logic below.
    const handleRequestSandbox = useCallback(
        (event, step) => {
            if (step?.button?.text !== 'Request Sandbox' || step?.button?.disabled) return;
            event.stopPropagation();
            requestSandbox();
        },
        [requestSandbox]
    );

    const setUpGuideData = {
        getStarted: GET_STARTED,
        tryOutProducts: TRY_OUT_PRODUCTS,
        goLiveReadiness: GO_LIVE_READINESS,
    };
    setUpGuideData.goLiveReadiness.steps[0].button.disabled = isRouteRestricted(
        'Billing',
        customer
    );

    const isOwner = userRole === 'company_owner';

    if (!isOwner || isSandboxAdminExist) {
        setUpGuideData.tryOutProducts.steps[0].button = null;
        setUpGuideData.tryOutProducts.steps[0].completed = true;
    }

    // Disable the "Request Sandbox" button until the company's agreement has been approved.
    const isAgreementApproved =
        !customer?.company?.meta_data?.agreement_status ||
        customer?.company?.meta_data?.agreement_status === 'approved';

    if (setUpGuideData.tryOutProducts.steps[0].button) {
        const requestSandboxButton = setUpGuideData.tryOutProducts.steps[0].button;
        if (!isAgreementApproved) {
            requestSandboxButton.disabled = true;
            requestSandboxButton.tooltip = REQUEST_SANDBOX_TOOLTIP.AGREEMENT_PENDING;
        } else if (guideStatus?.sentSandboxRequest) {
            requestSandboxButton.disabled = true;
            requestSandboxButton.tooltip = REQUEST_SANDBOX_TOOLTIP.REQUEST_SENT;
        } else {
            requestSandboxButton.disabled = false;
            requestSandboxButton.tooltip = '';
        }
    }

    // Keep "Create A Workflow" and "Customize Email" locked until the account is approved.
    [
        setUpGuideData.tryOutProducts.steps[1].button,
        setUpGuideData.goLiveReadiness.steps[2].button,
    ].forEach((button) => {
        if (button) {
            button.disabled = !isAgreementApproved;
            button.tooltip = isAgreementApproved
                ? ''
                : SETUP_GUIDE_TOOLTIP.ACCOUNT_APPROVAL_PENDING;
        }
    });

    const [setUpGuide, setSetUpGuide] = useState(setUpGuideData);

    useEffect(() => {
        if (guideStatus !== undefined) {
            const newSetUpGuide = { ...setUpGuide };

            const { getStarted, tryOutProducts, goLiveReadiness } = newSetUpGuide;

            const {
                agreementSigned,
                memberCreated,
                workflowAdded,
                sandboxPresented,
                accountActivated,
                customEmailPresented,
            } = guideStatus;

            getStarted.steps[0].completed = agreementSigned;
            getStarted.steps[1].completed = memberCreated;
            tryOutProducts.steps[0].completed = sandboxPresented;
            tryOutProducts.steps[1].completed = workflowAdded;
            goLiveReadiness.steps[0].completed = accountActivated;
            goLiveReadiness.steps[2].completed = customEmailPresented;

            setSetUpGuide(newSetUpGuide);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guideStatus]);

    return (
        <>
            <Box className={styles.prodSetupAccordionContainer}>
                <SetupAccordion setupData={setUpGuide.getStarted} />
            </Box>
            <Box className={styles.prodSetupAccordionContainer}>
                <SetupAccordion
                    setupData={setUpGuide.tryOutProducts}
                    onButtonClick={handleRequestSandbox}
                />
            </Box>
            <Box className={styles.prodSetupAccordionContainer}>
                <SetupAccordion setupData={setUpGuide.goLiveReadiness} />
            </Box>
        </>
    );
}

export default ProdSetup;
